import { NavLink, useLocation } from 'react-router-dom';
import { LayoutDashboard, CalendarCheck, Trophy, CalendarOff, Menu, ListTodo, Users, Wallet } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Fixed bottom navigation for mobile devices (< lg breakpoint).
 * Surfaces the 4 most-used routes per role + a "More" button that opens the
 * full sidebar drawer. Designed for one-thumb reachability: 56px tall,
 * clearly active state, ripple-style hover, safe-area padding for iOS.
 */
export default function MobileBottomNav({ onOpenSidebar, sidebarOpen }) {
  const { user } = useAuth();
  const role = user?.system_role;
  const { pathname } = useLocation();

  // Role-aware quick items. Employees see Dashboard + Attendance + Achievements
  // + Leave; managers/admins see Dashboard + Attendance + Employees + Leave.
  const items = (() => {
    if (role === 'super_admin' || role === 'management') {
      return [
        { path: '/dashboard', label: 'Home', icon: LayoutDashboard },
        { path: '/attendance', label: 'Attend', icon: CalendarCheck },
        { path: '/employees', label: 'Staff', icon: Users },
        { path: '/payroll', label: 'Payroll', icon: Wallet },
      ];
    }
    if (role === 'branch_manager') {
      return [
        { path: '/dashboard', label: 'Home', icon: LayoutDashboard },
        { path: '/attendance', label: 'Attend', icon: CalendarCheck },
        { path: '/employees', label: 'Staff', icon: Users },
        { path: '/leave', label: 'Leave', icon: CalendarOff },
      ];
    }
    // employee & brand staff
    return [
      { path: '/dashboard', label: 'Home', icon: LayoutDashboard },
      { path: '/attendance', label: 'Attend', icon: CalendarCheck },
      { path: '/achievements', label: 'Brand', icon: Trophy },
      { path: '/leave', label: 'Leave', icon: CalendarOff },
    ];
  })();

  return (
    <nav
      className="mobile-bottom-nav xl:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-neutral-200 shadow-[0_-2px_8px_rgba(0,0,0,0.06)] pb-[env(safe-area-inset-bottom)]"
      data-testid="mobile-bottom-nav"
      aria-label="Quick navigation"
    >
      <div className="grid grid-cols-5 h-16">
        {items.map(({ path, label, icon: Icon }) => (
          <NavLink
            key={path}
            to={path}
            data-testid={`bottomnav-${label.toLowerCase()}`}
            className={({ isActive }) =>
              `relative flex flex-col items-center justify-center gap-1 text-xs font-semibold transition-colors active:bg-neutral-100 ${
                isActive ? 'text-red-600 bg-red-50/60 border-t-2 border-red-600' : 'text-neutral-600 hover:text-neutral-900 border-t-2 border-transparent'
              }`
            }
          >
            {({ isActive }) => (
              <>
                <Icon className={`h-5 w-5 ${isActive ? 'text-red-600' : 'text-neutral-500'}`} />
                <span className="leading-none">{label}</span>
              </>
            )}
          </NavLink>
        ))}
        <button
          type="button"
          onClick={onOpenSidebar}
          data-testid="bottomnav-more"
          aria-label="More navigation" aria-expanded={sidebarOpen}
          className={`flex flex-col items-center justify-center gap-1 text-xs font-semibold hover:bg-neutral-100 border-t-2 ${!items.some(item => pathname.startsWith(item.path)) ? 'text-red-600 border-red-600 bg-red-50/60' : 'text-neutral-600 border-transparent'}`}
        >
          <Menu className="h-5 w-5 text-neutral-500" />
          <span className="leading-none">More</span>
        </button>
      </div>
    </nav>
  );
}
